/** PKCE (RFC 7636) — chỉ hỗ trợ S256, `code_verifier`/`state` lưu tạm ở `sessionStorage`. */

const CODE_VERIFIER_KEY = 'oidc_pkce_code_verifier'
const STATE_KEY = 'oidc_pkce_state'
const NONCE_KEY = 'oidc_pkce_nonce'
const RETURN_TO_KEY = 'oidc_pkce_return_to'

const base64UrlEncode = (buffer: ArrayBuffer | Uint8Array): string => {
  const bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer)
  let binary = ''
  bytes.forEach(byte => {
    binary += String.fromCharCode(byte)
  })
  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '')
}

const randomBytes = (length: number): Uint8Array => {
  const array = new Uint8Array(length)
  window.crypto.getRandomValues(array)
  return array
}

/** Sinh code_verifier ngẫu nhiên bằng Web Crypto API (32 byte -> 43 ký tự base64url, đúng RFC 7636). */
export const generateCodeVerifier = (): string => base64UrlEncode(randomBytes(32))

/** Sinh code_challenge = BASE64URL(SHA-256(code_verifier)) — chỉ hỗ trợ S256. */
export const generateCodeChallenge = async (verifier: string): Promise<string> => {
  const encoder = new TextEncoder()
  const digest = await window.crypto.subtle.digest('SHA-256', encoder.encode(verifier))
  return base64UrlEncode(digest)
}

/** Sinh state ngẫu nhiên (chống CSRF cho Authorization Code flow). */
export const generateState = (): string => base64UrlEncode(randomBytes(16))

/** Sinh nonce ngẫu nhiên (correlate id_token với đúng authorization request, chống replay/injection). */
export const generateNonce = (): string => base64UrlEncode(randomBytes(16))

export interface PkceTransaction {
  codeVerifier: string
  codeChallenge: string
  state: string
  nonce: string
}

/** Tạo 1 giao dịch PKCE mới (verifier + challenge S256 + state + nonce), chưa lưu trữ. */
export const createPkceTransaction = async (): Promise<PkceTransaction> => {
  const codeVerifier = generateCodeVerifier()
  const codeChallenge = await generateCodeChallenge(codeVerifier)
  const state = generateState()
  const nonce = generateNonce()
  return { codeVerifier, codeChallenge, state, nonce }
}

/**
 * Lưu tạm code_verifier/state (sessionStorage) trước khi redirect sang `/connect/authorize`.
 * `returnTo` (tuỳ chọn): đường dẫn SPA muốn quay lại sau khi login thành công (thay cho
 * `location.state.from` mà Login.tsx cũ dùng — redirect toàn trang không giữ được React Router
 * state, phải tự lưu).
 */
export const savePkceTransaction = (
  transaction: Pick<PkceTransaction, 'codeVerifier' | 'state' | 'nonce'>,
  returnTo?: string,
): void => {
  sessionStorage.setItem(CODE_VERIFIER_KEY, transaction.codeVerifier)
  sessionStorage.setItem(STATE_KEY, transaction.state)
  sessionStorage.setItem(NONCE_KEY, transaction.nonce)
  if (returnTo) {
    sessionStorage.setItem(RETURN_TO_KEY, returnTo)
  } else {
    sessionStorage.removeItem(RETURN_TO_KEY)
  }
}

export const getStoredCodeVerifier = (): string | null =>
  sessionStorage.getItem(CODE_VERIFIER_KEY)

export const getStoredState = (): string | null => sessionStorage.getItem(STATE_KEY)

export const getStoredNonce = (): string | null => sessionStorage.getItem(NONCE_KEY)

export const getStoredReturnTo = (): string | null => sessionStorage.getItem(RETURN_TO_KEY)

/** Xoá dữ liệu PKCE tạm — PHẢI gọi ngay sau khi exchange code (thành công hoặc thất bại). */
export const clearPkceTransaction = (): void => {
  sessionStorage.removeItem(CODE_VERIFIER_KEY)
  sessionStorage.removeItem(STATE_KEY)
  sessionStorage.removeItem(NONCE_KEY)
  sessionStorage.removeItem(RETURN_TO_KEY)
}

const base64UrlDecode = (segment: string): string => {
  const base64 = segment.replace(/-/g, '+').replace(/_/g, '/')
  const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=')
  return atob(padded)
}

/** Đọc claim `nonce` từ id_token — CHỈ decode payload, KHÔNG verify chữ ký (đủ cho correlation nonce; không dùng thay cho validator JWT đầy đủ). */
export const decodeIdTokenNonce = (idToken: string): string | null => {
  try {
    const [, payload] = idToken.split('.')
    if (!payload) return null
    const claims = JSON.parse(base64UrlDecode(payload))
    return typeof claims.nonce === 'string' ? claims.nonce : null
  } catch {
    return null
  }
}
